/*
 * Restore homepage motion — restrained, homepage-only progressive enhancement.
 * The page is always visible by default. Only targets safely below the viewport are
 * pre-armed, which prevents late-loaded JavaScript from causing visible-to-hidden flashes.
 */
(() => {
  const STATE_KEY = '__restoreHomepageMotionState';
  const EVENTS_KEY = '__restoreHomepageMotionEventsInstalled';
  const ROOT_SELECTOR = '.template-index';
  const DONE_ATTRIBUTE = 'data-rph-motion-done';

  const compact = (nodes) => nodes.filter(Boolean);
  const selectAll = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const getRoot = () => document.querySelector(ROOT_SELECTOR);

  const clearProperties = (gsap, nodes) => {
    const targets = compact(nodes);
    if (targets.length) gsap.set(targets, { clearProps: 'opacity,visibility,transform,willChange' });
  };

  const clearState = () => {
    const state = window[STATE_KEY];
    if (!state) return;
    state.context?.revert();
    state.media?.revert();
    window[STATE_KEY] = undefined;
  };

  const safelyBelowViewport = (element) => element.getBoundingClientRect().top > window.innerHeight + 32;

  const run = () => {
    clearState();

    const root = getRoot();
    const { gsap, ScrollTrigger } = window;
    if (!root || !gsap || !ScrollTrigger) return;

    gsap.registerPlugin(ScrollTrigger);
    const media = gsap.matchMedia();

    media.add(
      {
        desktop: '(min-width: 861px)',
        mobile: '(max-width: 860px)',
        reducedMotion: '(prefers-reduced-motion: reduce)',
      },
      (conditions) => {
        const { desktop, reducedMotion } = conditions.conditions;
        const context = gsap.context(() => {
          const allMotionTargets = selectAll([
            '.rph-hero__media',
            '.rph-hero .rph-eyebrow',
            '.rph-hero h1',
            '.rph-hero__lead',
            '.rph-hero__actions',
            '.rph-reassurance',
            '.rpp-product-portfolio__header',
            '.rpp-product-portfolio__card',
            '.rph-how-journey__card',
            '.rph-topical__media',
            '.rph-topical__content > .rph-eyebrow',
            '.rph-topical__content > h2',
            '.rph-ingredients article',
            '.rph-provider__copy > .rph-eyebrow',
            '.rph-provider__copy > h2',
            '.rph-provider__image',
            '.rph-insights .rph-section-intro',
            '.rph-insight-card',
            'footer .rph-footer-signup-section',
          ].join(','), document);

          if (reducedMotion) {
            clearProperties(gsap, allMotionTargets);
            return;
          }

          const distance = desktop ? 24 : 16;
          const cardDistance = desktop ? 24 : 16;
          const duration = desktop ? 0.82 : 0.72;

          /*
           * Pre-arm only targets that have not yet been displayed and are safely offscreen.
           * This deliberately avoids `gsap.from()` / autoAlpha starts on content a visitor
           * may already be looking at, eliminating late-load FOUC and visible blinking.
           */
          const armReveal = (trigger, candidates, options = {}) => {
            const nodes = compact(Array.isArray(candidates) ? candidates : [candidates])
              .filter((node) => !node.hasAttribute(DONE_ATTRIBUTE))
              .filter(safelyBelowViewport);
            if (!trigger || !nodes.length) return;

            gsap.set(nodes, { autoAlpha: 0, y: options.y ?? distance, willChange: 'transform,opacity' });
            const complete = () => {
              nodes.forEach((node) => node.setAttribute(DONE_ATTRIBUTE, ''));
              clearProperties(gsap, nodes);
            };

            gsap.to(nodes, {
              autoAlpha: 1,
              y: 0,
              duration: options.duration ?? duration,
              ease: 'power2.out',
              stagger: options.stagger ?? 0,
              onComplete: complete,
              scrollTrigger: {
                trigger,
                start: 'top 85%',
                once: true,
                invalidateOnRefresh: true,
              },
            });
          };

          // Hero stays visible at all times. Its small transform-only settle avoids a late opacity flash.
          const hero = root.querySelector('#rph-hero');
          if (hero && window.scrollY < 24) {
            const heroMedia = hero.querySelector('.rph-hero__media');
            const heroCopy = compact([
              hero.querySelector('.rph-eyebrow'),
              hero.querySelector('h1'),
              hero.querySelector('.rph-hero__lead'),
              hero.querySelector('.rph-hero__actions'),
              hero.querySelector('.rph-reassurance'),
            ]);
            const heroTimeline = gsap.timeline({ defaults: { ease: 'power2.out' } });
            heroTimeline
              .from(heroMedia, { scale: desktop ? 1.012 : 1.006, duration: desktop ? 0.78 : 0.58 })
              .from(heroCopy, { y: desktop ? 10 : 7, duration: desktop ? 0.62 : 0.50, stagger: 0.055 }, '-=0.54');
          }

          const portfolio = root.querySelector('.rpp-product-portfolio');
          if (portfolio) {
            armReveal(portfolio, portfolio.querySelector('.rpp-product-portfolio__header'), { y: distance });
            armReveal(portfolio, selectAll('.rpp-product-portfolio__card', portfolio), {
              y: cardDistance,
              stagger: desktop ? 0.075 : 0.055,
            });
          }

          const journey = root.querySelector('.rph-how--journey');
          if (journey) {
            armReveal(journey, selectAll('.rph-how-journey__card', journey), {
              y: cardDistance,
              stagger: desktop ? 0.10 : 0.07,
            });
          }

          selectAll('.rph-topical', root).forEach((section) => {
            armReveal(section, compact([
              section.querySelector('.rph-topical__media'),
              section.querySelector('.rph-topical__content > .rph-eyebrow'),
              section.querySelector('.rph-topical__content > h2'),
            ]), { y: distance, stagger: 0.07 });
          });

          const science = root.querySelector('.rph-science');
          if (science) {
            armReveal(science, selectAll('.rph-ingredients article', science), {
              y: cardDistance,
              stagger: desktop ? 0.07 : 0.055,
            });
          }

          const provider = root.querySelector('.rph-provider');
          if (provider) {
            armReveal(provider, compact([
              provider.querySelector('.rph-provider__copy > .rph-eyebrow'),
              provider.querySelector('.rph-provider__copy > h2'),
              provider.querySelector('.rph-provider__image'),
            ]), { y: distance, stagger: 0.07 });
          }

          const insights = root.querySelector('.rph-insights');
          if (insights) {
            armReveal(insights, compact([
              insights.querySelector('.rph-section-intro'),
              ...selectAll('.rph-insight-card', insights),
            ]), { y: cardDistance, stagger: desktop ? 0.07 : 0.055 });
          }

          const footer = document.querySelector('footer');
          if (footer) {
            armReveal(footer, footer.querySelector('.rph-footer-signup-section'), {
              y: desktop ? 18 : 12,
              duration: desktop ? 0.72 : 0.62,
            });
          }
        }, document);

        return () => {
          context.revert();
          clearProperties(gsap, allMotionTargets);
        };
      },
    );

    window[STATE_KEY] = { media };
    requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  const boot = () => {
    run();
    if (window[EVENTS_KEY]) return;
    window[EVENTS_KEY] = true;

    const reinitialize = () => {
      if (getRoot()) run();
    };

    document.addEventListener('shopify:section:load', reinitialize);
    document.addEventListener('shopify:section:unload', reinitialize);
    document.addEventListener('shopify:section:reorder', reinitialize);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && window.ScrollTrigger) window.ScrollTrigger.refresh();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
